/**
 * This is the starting point of your application.
 * Feature Hub Apps will use this file to bootstrap the app.
 */
import React from 'react';

import type { EditorialColumnsProps } from '../types';
import { EditorialColumns } from './components/EditorialColumns';
import { transformCmsDataToProps } from './utils/transformCmsDataToProps';
import type { FaceContent, FalconContent } from './utils/normalize-falcon-data';
import { normalizeContent } from './utils/normalize-falcon-data';
import { useLogger } from './Context';
import type { ContentService, GfaServiceConfigProvider } from './feature-service-types';

interface FeatureAppProps {
  configProvider?: GfaServiceConfigProvider;
  contentService: ContentService;
  initialContent?: EditorialColumnsProps;
}

const FeatureApp: React.FC<FeatureAppProps> = ({
  contentService,
  initialContent,
}: FeatureAppProps) => {
  const logger = useLogger();
  const [content, setContent] = React.useState<EditorialColumnsProps | null>(
    transformCmsDataToProps(
      normalizeContent(
        (contentService.getContent() as FalconContent | FaceContent) ?? initialContent,
        logger,
      ),
      logger,
    ),
  );

  React.useEffect(() => {
    const listener = () => {
      setContent(
        transformCmsDataToProps(
          normalizeContent(
            (contentService.getContent() as FalconContent | FaceContent) ?? initialContent,
            logger,
          ),
          logger,
        ),
      );
    };
    contentService.onContentChange(listener);

    return () => {
      contentService.removeOnContentChange(listener);
    };
  }, [contentService, initialContent]);

  if (!content) {
    logger?.info('no content');
    return null;
  }

  return <EditorialColumns {...content} />;
};

export default FeatureApp;
